import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail, Lock, User, Loader2, ArrowRight,
  Phone, Eye, EyeOff, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

const ALLOWED_DOMAINS = ["gmail.com", "outlook.com", "yahoo.com"];

const isAllowedEmail = (email) => {
  const parts = email.trim().toLowerCase().split("@");
  return parts.length === 2 && ALLOWED_DOMAINS.includes(parts[1]);
};

function EmailSignupForm({ redirectTo }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const rawDigits = phone.replace(/\D/g, "");
  const emailTouched = email.length > 0;
  const emailOk = isAllowedEmail(email);
  const phoneOk = rawDigits.length === 10 && "6789".includes(rawDigits[0]);
  const passOk = password.length >= 6;
  const matchOk = password === confirm;
  const canSubmit = name.trim().length >= 2 && emailOk && phoneOk && passOk && matchOk;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!emailOk) errs.email = "Please use a Gmail, Outlook or Yahoo email address.";
    if (rawDigits.length !== 10) errs.phone = "Phone number must be exactly 10 digits.";
    else if (!"6789".includes(rawDigits[0])) errs.phone = "Indian mobile numbers must start with 6, 7, 8 or 9.";
    if (!passOk) errs.password = "Password must be at least 6 characters.";
    if (!matchOk) errs.confirm = "Passwords do not match.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: rawDigits,
        password,
      });
      setUser(data);
      toast.success(`Welcome to Language Nest, ${data.name}!`);
      navigate(redirectTo || "/survey");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(detail);
      // Surface API error next to the most likely field
      if (/email/i.test(detail)) setErrors((e) => ({ ...e, email: detail }));
      else if (/phone/i.test(detail)) setErrors((e) => ({ ...e, phone: detail }));
    } finally {
      setLoading(false);
    }
  };

  const Err = ({ msg }) => msg ? (
    <p className="mt-1.5 text-xs text-red-600 font-semibold" data-testid="register-field-error">{msg}</p>
  ) : null;

  return (
    <motion.form
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      onSubmit={submit} className="space-y-4" data-testid="register-form"
    >
      <div>
        <Label htmlFor="name" className="text-brand-navy font-semibold">Full name</Label>
        <div className="mt-2 relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
          <Input
            id="name" type="text" required value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Priya Sharma"
            className="pl-11 h-12 rounded-xl border-brand-navy/15"
            data-testid="register-name-input"
          />
        </div>
        <Err msg={errors.name} />
      </div>

      <div>
        <Label htmlFor="email" className="text-brand-navy font-semibold">Email</Label>
        <div className="mt-2 relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
          <Input
            id="email" type="email" required value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((x) => ({ ...x, email: undefined })); }}
            placeholder="you@gmail.com"
            className={`pl-11 h-12 rounded-xl border-brand-navy/15 ${emailTouched && !emailOk ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            data-testid="register-email-input"
          />
        </div>
        <p className="mt-1.5 text-xs text-brand-navy/50">Allowed: <b>@gmail.com</b>, <b>@outlook.com</b>, <b>@yahoo.com</b></p>
        <Err msg={errors.email} />
      </div>

      <div>
        <Label htmlFor="phone" className="text-brand-navy font-semibold">Mobile number</Label>
        <div className="mt-2 flex gap-2">
          <div className="h-12 px-3 rounded-xl border border-brand-navy/15 bg-brand-muted-bg flex items-center gap-1.5 font-semibold text-brand-navy">
            <span className="text-lg">🇮🇳</span>
            <span>+91</span>
          </div>
          <div className="relative flex-1">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
            <Input
              id="phone" type="tel" inputMode="numeric" pattern="[0-9]*"
              maxLength={10} required value={rawDigits}
              onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setErrors((x) => ({ ...x, phone: undefined })); }}
              placeholder="10-digit mobile number"
              className={`pl-11 h-12 rounded-xl border-brand-navy/15 ${phone.length > 0 && !phoneOk ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              data-testid="register-phone-input"
            />
          </div>
        </div>
        <Err msg={errors.phone} />
      </div>

      <div>
        <Label htmlFor="password" className="text-brand-navy font-semibold">Password</Label>
        <div className="mt-2 relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
          <Input
            id="password" type={showPass ? "text" : "password"} required
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Min 6 characters"
            className="pl-11 pr-12 h-12 rounded-xl border-brand-navy/15"
            data-testid="register-password-input"
          />
          <button
            type="button" onClick={() => setShowPass((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/50 hover:text-brand-red transition p-1"
            aria-label={showPass ? "Hide password" : "Show password"}
            data-testid="register-password-toggle"
          >
            {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <Err msg={errors.password} />
      </div>

      <div>
        <Label htmlFor="confirm" className="text-brand-navy font-semibold">Confirm password</Label>
        <div className="mt-2 relative">
          <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" />
          <Input
            id="confirm" type={showConfirm ? "text" : "password"} required
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter password"
            className={`pl-11 pr-12 h-12 rounded-xl border-brand-navy/15 ${confirm && !matchOk ? "border-red-500" : ""}`}
            data-testid="register-confirm-input"
          />
          <button
            type="button" onClick={() => setShowConfirm((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/50 hover:text-brand-red transition p-1"
            aria-label={showConfirm ? "Hide password" : "Show password"}
            data-testid="register-confirm-toggle"
          >
            {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <Err msg={confirm && !matchOk ? "Passwords do not match" : errors.confirm} />
      </div>

      <Button
        type="submit" disabled={!canSubmit || loading}
        data-testid="register-submit-btn"
        className="w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold brand-shadow btn-scale disabled:opacity-50"
      >
        {loading
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <>Create account <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </motion.form>
  );
}

export default function RegisterPage() {
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get("redirect");

  return (
    <AuthShell
      eyebrow="Join the nest"
      title="Start your journey in 30 seconds"
      subtitle="Book demos, save progress, and unlock your personalised learning roadmap."
    >
      <EmailSignupForm redirectTo={redirectParam} />

      <div className="my-6 flex items-center gap-4">
        <div className="flex-1 h-px bg-brand-navy/10" />
        <span className="text-xs uppercase tracking-widest text-brand-navy/40 font-semibold">or</span>
        <div className="flex-1 h-px bg-brand-navy/10" />
      </div>

      <GoogleAuthButton redirectTo={redirectParam} testId="register-google-btn" />

      <p className="mt-8 text-center text-sm text-brand-navy/60">
        Already a member?{" "}
        <Link
          to={redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : "/login"}
          data-testid="register-goto-login"
          className="text-brand-red font-semibold hover:underline"
        >
          Log in
        </Link>
      </p>
    </AuthShell>
  );
}
